import {
    math,
    ADDRESS_CLAMP_TO_EDGE,
    ASPECT_MANUAL,
    FILTER_NEAREST,
    PIXELFORMAT_RGBA8,
    PIXELFORMAT_RGBA16F,
    PIXELFORMAT_DEPTH,
    PROJECTION_ORTHOGRAPHIC,
    PROJECTION_PERSPECTIVE,
    TONEMAP_ACES,
    TONEMAP_ACES2,
    TONEMAP_FILMIC,
    TONEMAP_HEJL,
    TONEMAP_LINEAR,
    TONEMAP_NEUTRAL,
    BoundingBox,
    Color,
    Entity,
    Mat4,
    Ray,
    RenderPass,
    RenderPassForward,
    RenderTarget,
    Texture,
    Vec3,
    Vec4
} from 'playcanvas';

import { PointerController } from './controllers';
import { Element, ElementType } from './element';
import { Picker } from './picker';
import { Serializer } from './serializer';
import { vertexShader, fragmentShader } from './shaders/blit-shader';
import { vertexShader as stereoBlitVs, fragmentShader as stereoBlitFs } from './shaders/stereo-blit-shader';
import { Splat } from './splat';
import { TweenValue } from './tween-value';
import { ShaderQuad, SimpleRenderPass } from './utils/simple-render-pass';

// work globals
const forwardVec = new Vec3();
const cameraPosition = new Vec3();
const ray = new Ray();
const vec = new Vec3();
const vecb = new Vec3();
const va = new Vec3();
const m = new Mat4();
const v4 = new Vec4();
const stereoShearMat = new Mat4();
const stereoProjPreShear = new Mat4();

// modulo dealing with negative numbers
const mod = (n: number, m: number) => ((n % m) + m) % m;

class Camera extends Element {
    /**
     * Calculate the forward vector given azimuth and elevation angles.
     *
     * @param {Vec3} result - The Vec3 to store the result in.
     * @param {number} azim - Azimuth angle in degrees.
     * @param {number} elev - Elevation angle in degrees.
     */
    static calcForwardVec(result: Vec3, azim: number, elev: number) {
        const ex = elev * math.DEG_TO_RAD;
        const ey = azim * math.DEG_TO_RAD;
        const s1 = Math.sin(-ex);
        const c1 = Math.cos(-ex);
        const s2 = Math.sin(-ey);
        const c2 = Math.cos(-ey);
        result.set(-c1 * s2, s1, c1 * c2);
    }

    controller: PointerController;
    focalPointTween = new TweenValue({ x: 0, y: 0.5, z: 0 });
    azimElevTween = new TweenValue({ azim: 30, elev: -15 });
    distanceTween = new TweenValue({ distance: 1 });

    minElev = -90;
    maxElev = 90;

    sceneRadius = 1;

    flySpeed = 1;

    controlMode: 'orbit' | 'fly' = 'orbit';

    // during fly-mode look, stores the camera position that must stay fixed
    // while the azim/elev tween smoothly converges
    lookCameraPos: Vec3 | null = null;

    picker: Picker;

    mainCamera: Entity;

    mainTarget?: RenderTarget;
    splatTarget?: RenderTarget;
    colorTarget?: RenderTarget;
    workTarget?: RenderTarget;

    // Render passes
    clearPass: RenderPass;
    mainPass: RenderPassForward;
    splatPass: RenderPassForward;
    gizmoPass: RenderPassForward;
    finalPass: SimpleRenderPass;

    /** Side-by-side: interocular half-offset = {@link stereoRatio} × focal length (camera local +X, parent space). */
    sideBySide = false;
    /** Dimensionless; effective offset along camera +X is `stereoRatio * stereoFocalLength()`. */
    stereoRatio = 0;

    /** Depth of field. */
    conv = 0;

    readonly stereoCenterPosition = new Vec3();

    private _layoutMode: 'mono' | 'stereo' = 'mono';

    /** Which eye is drawing during stereo pass chain (for overlay RT selection). */
    private _stereoEyePass: 'left' | 'right' | null = null;

    leftMainTarget?: RenderTarget;
    leftSplatTarget?: RenderTarget;
    leftColorTarget?: RenderTarget;
    leftWorkTarget?: RenderTarget;
    rightMainTarget?: RenderTarget;
    rightSplatTarget?: RenderTarget;
    rightColorTarget?: RenderTarget;
    rightWorkTarget?: RenderTarget;

    leftClearPass: RenderPass;
    leftMainPass: RenderPassForward;
    leftSplatPass: RenderPassForward;
    leftGizmoPass: RenderPassForward;
    rightClearPass: RenderPass;
    rightMainPass: RenderPassForward;
    rightSplatPass: RenderPassForward;
    rightGizmoPass: RenderPassForward;
    stereoFinalPass: SimpleRenderPass;

    // overridden target size
    targetSizeOverride: { width: number, height: number } = null;

    renderOverlays = true;

    updateCameraUniforms: () => void;

    constructor() {
        super(ElementType.camera);

        // create the camera entity
        this.mainCamera = new Entity('Camera');
        this.mainCamera.addComponent('camera');
    }

    // ortho
    set ortho(value: boolean) {
        if (value !== this.ortho) {
            this.camera.projection = value ? PROJECTION_ORTHOGRAPHIC : PROJECTION_PERSPECTIVE;
            this.scene.events.fire('camera.ortho', value);
        }
    }

    get ortho() {
        return this.camera.projection === PROJECTION_ORTHOGRAPHIC;
    }

    // fov
    set fov(value: number) {
        this.camera.fov = value;
    }

    get fov() {
        return this.camera.fov;
    }

    // tonemapping
    set tonemapping(value: string) {
        const mapping: Record<string, number> = {
            linear: TONEMAP_LINEAR,
            neutral: TONEMAP_NEUTRAL,
            aces: TONEMAP_ACES,
            aces2: TONEMAP_ACES2,
            filmic: TONEMAP_FILMIC,
            hejl: TONEMAP_HEJL
        };

        const tvalue = mapping[value];

        if (tvalue !== undefined && tvalue !== this.camera.toneMapping) {
            this.camera.toneMapping = tvalue;
            this.scene.events.fire('camera.tonemapping', value);
        }
    }

    get tonemapping() {
        switch (this.camera.toneMapping) {
            case TONEMAP_LINEAR: return 'linear';
            case TONEMAP_NEUTRAL: return 'neutral';
            case TONEMAP_ACES: return 'aces';
            case TONEMAP_ACES2: return 'aces2';
            case TONEMAP_FILMIC: return 'filmic';
            case TONEMAP_HEJL: return 'hejl';
        }
        return 'linear';
    }

    // near clip
    set near(value: number) {
        this.camera.nearClip = value;
    }

    get near() {
        return this.camera.nearClip;
    }

    // far clip
    set far(value: number) {
        this.camera.farClip = value;
    }

    get far() {
        return this.camera.farClip;
    }

    // focal point
    get focalPoint() {
        const t = this.focalPointTween.target;
        return new Vec3(t.x, t.y, t.z);
    }

    // azimuth, elevation
    get azimElev() {
        return this.azimElevTween.target;
    }

    get azim() {
        return this.azimElev.azim;
    }

    get elevation() {
        return this.azimElev.elev;
    }

    get distance() {
        return this.distanceTween.target.distance;
    }

    setFocalPoint(point: Vec3, dampingFactorFactor: number = 1) {
        this.lookCameraPos = null;
        this.focalPointTween.goto(point, dampingFactorFactor * this.scene.config.controls.dampingFactor);
    }

    // Fly mode: rotate camera around itself, keeping the camera position fixed
    look(dx: number, dy: number) {
        const sensitivity = this.scene.config.controls.orbitSensitivity;
        const d = this.distance * this.sceneRadius / this.fovFactor;

        Camera.calcForwardVec(forwardVec, this.azim, this.elevation);
        const cameraPos = this.focalPoint.add(forwardVec.clone().mulScalar(d));

        const azim = this.azim - dx * sensitivity;
        const elev = this.elevation - dy * sensitivity;

        Camera.calcForwardVec(forwardVec, azim, elev);
        const focalPoint = cameraPos.clone().sub(forwardVec.clone().mulScalar(d));

        this.setAzimElev(azim, elev);
        this.focalPointTween.goto(focalPoint, this.scene.config.controls.dampingFactor);
        this.lookCameraPos = cameraPos;
    }

    setAzimElev(azim: number, elev: number, dampingFactorFactor: number = 1) {
        // clamp
        azim = mod(azim, 360);
        elev = Math.max(this.minElev, Math.min(this.maxElev, elev));

        const t = this.azimElevTween;
        t.goto({ azim, elev }, dampingFactorFactor * this.scene.config.controls.dampingFactor);

        // handle wraparound
        if (t.source.azim - azim < -180) {
            t.source.azim += 360;
        } else if (t.source.azim - azim > 180) {
            t.source.azim -= 360;
        }

        // return to perspective mode on rotation
        this.ortho = false;
    }

    setDistance(distance: number, dampingFactorFactor: number = 1) {
        this.lookCameraPos = null;

        const controls = this.scene.config.controls;

        // clamp
        distance = Math.max(controls.minZoom, Math.min(controls.maxZoom, distance));

        const t = this.distanceTween;
        t.goto({ distance }, dampingFactorFactor * controls.dampingFactor);
    }

    setPose(position: Vec3, target: Vec3, dampingFactorFactor: number = 1) {
        vec.sub2(target, position);
        const l = vec.length();
        const azim = Math.atan2(-vec.x / l, -vec.z / l) * math.RAD_TO_DEG;
        const elev = Math.asin(vec.y / l) * math.RAD_TO_DEG;
        this.setFocalPoint(target, dampingFactorFactor);
        this.setAzimElev(azim, elev, dampingFactorFactor);
        this.setDistance(l / this.sceneRadius * this.fovFactor, dampingFactorFactor);
    }

    // transform the world space coordinate to normalized screen coordinate
    worldToScreen(world: Vec3, screen: Vec3) {
        const { camera } = this;
        m.mul2(camera.projectionMatrix, camera.viewMatrix);

        v4.set(world.x, world.y, world.z, 1);
        m.transformVec4(v4, v4);

        screen.x = v4.x / v4.w * 0.5 + 0.5;
        screen.y = 1.0 - (v4.y / v4.w * 0.5 + 0.5);
        screen.z = v4.z / v4.w;
    }

    add() {
        const { camera, scene } = this;

        scene.cameraRoot.addChild(this.mainCamera);

        // configure camera to render all layers
        this.mainCamera.camera.layers = [
            scene.worldLayer.id,
            scene.splatLayer.id,
            scene.gizmoLayer.id
        ];

        // use manual aspect ratio mode so we can set it based on targetSize
        camera.aspectRatioMode = ASPECT_MANUAL;

        // create render passes
        const device = scene.graphicsDevice;
        const { app } = scene;
        const renderer = app.renderer;
        const composition = app.scene.layers;

        this.clearPass = new RenderPass(device);
        this.mainPass = new RenderPassForward(device, composition, app.scene, renderer);
        this.splatPass = new RenderPassForward(device, composition, app.scene, renderer);
        this.gizmoPass = new RenderPassForward(device, composition, app.scene, renderer);
        this.finalPass = new SimpleRenderPass(device,
            new ShaderQuad(device, vertexShader, fragmentShader, 'final-blit'), {
                vars: () => {
                    return {
                        srcTexture: this.mainTarget!.colorBuffer
                    };
                }
            });

        this.leftClearPass = new RenderPass(device);
        this.leftMainPass = new RenderPassForward(device, composition, app.scene, renderer);
        this.leftSplatPass = new RenderPassForward(device, composition, app.scene, renderer);
        this.leftGizmoPass = new RenderPassForward(device, composition, app.scene, renderer);
        this.rightClearPass = new RenderPass(device);
        this.rightMainPass = new RenderPassForward(device, composition, app.scene, renderer);
        this.rightSplatPass = new RenderPassForward(device, composition, app.scene, renderer);
        this.rightGizmoPass = new RenderPassForward(device, composition, app.scene, renderer);
        this.stereoFinalPass = new SimpleRenderPass(device,
            new ShaderQuad(device, stereoBlitVs, stereoBlitFs, 'stereo-final-blit'), {
                vars: () => {
                    const { graphicsDevice } = this.scene.app;
                    return {
                        leftTexture: this.leftMainTarget!.colorBuffer,
                        rightTexture: this.rightMainTarget!.colorBuffer,
                        deviceSize: [graphicsDevice.width, graphicsDevice.height]
                    };
                }
            });

        const target = document.getElementById('canvas-container');
        this.controller = new PointerController(this, target);

        // apply scene config
        const config = scene.config;
        const controls = config.controls;

        this.minElev = (controls.minPolarAngle * 180) / Math.PI - 90;
        this.maxElev = (controls.maxPolarAngle * 180) / Math.PI - 90;

        // tonemapping
        camera.toneMapping = {
            linear: TONEMAP_LINEAR,
            filmic: TONEMAP_FILMIC,
            hejl: TONEMAP_HEJL,
            aces: TONEMAP_ACES,
            aces2: TONEMAP_ACES2,
            neutral: TONEMAP_NEUTRAL
        }[config.camera.toneMapping];

        // exposure
        scene.app.scene.exposure = config.camera.exposure;

        this.fov = config.camera.fov;

        // initial camera position and orientation
        this.setAzimElev(controls.initialAzim, controls.initialElev, 0);
        this.setDistance(controls.initialZoom, 0);

        // picker
        this.picker = new Picker(scene);

        scene.events.on('scene.boundChanged', this.onBoundChanged, this);

        scene.events.on('custom.conv.decrease', this.onConvDecrease, this);
        scene.events.on('custom.conv.increase', this.onConvIncrease, this);
        scene.events.on('custom.stereoRatio.decrease', this.onStereoRatioDecrease, this);
        scene.events.on('custom.stereoRatio.increase', this.onStereoRatioIncrease, this);

        // prepare camera-specific uniforms
        this.updateCameraUniforms = () => {
            const device = scene.graphicsDevice;
            const entity = this.mainCamera;
            const camera = entity.camera;

            const set = (name: string, vec: Vec3) => {
                device.scope.resolve(name).setValue([vec.x, vec.y, vec.z]);
            };

            // get frustum corners in world space
            const points = camera.camera.getFrustumCorners(-100);
            const worldTransform = this.worldTransform;
            for (let i = 0; i < points.length; i++) {
                worldTransform.transformPoint(points[i], points[i]);
            }

            // near
            if (camera.projection === PROJECTION_PERSPECTIVE) {
                // perspective
                set('near_origin', worldTransform.getTranslation());
                set('near_x', Vec3.ZERO);
                set('near_y', Vec3.ZERO);
            } else {
                // orthographic
                set('near_origin', points[3]);
                set('near_x', va.sub2(points[0], points[3]));
                set('near_y', va.sub2(points[2], points[3]));
            }

            // far
            set('far_origin', points[7]);
            set('far_x', va.sub2(points[4], points[7]));
            set('far_y', va.sub2(points[6], points[7]));
        };

        // temp control of camera start
        const url = new URL(location.href);
        const focal = url.searchParams.get('focal');
        if (focal) {
            const parts = focal.toString().split(',');
            if (parts.length === 3) {
                this.setFocalPoint(new Vec3(parseFloat(parts[0]), parseFloat(parts[1]), parseFloat(parts[2])), 0);
            }
        }
        const angles = url.searchParams.get('angles');
        if (angles) {
            const parts = angles.toString().split(',');
            if (parts.length === 2) {
                this.setAzimElev(parseFloat(parts[0]), parseFloat(parts[1]), 0);
            }
        }
        const distance = url.searchParams.get('distance');
        if (distance) {
            this.setDistance(parseFloat(distance), 0);
        }
        const sbs = url.searchParams.get('sideBySide');
        if (sbs === '1' || sbs === 'true') {
            this.sideBySide = true;
        }
        const ratioParam = url.searchParams.get('stereoRatio');
        if (ratioParam !== null && ratioParam !== '') {
            const v = parseFloat(ratioParam);
            if (Number.isFinite(v)) {
                this.stereoRatio = v;
            }
        }
        const deltxLegacy = url.searchParams.get('deltxX');
        if (ratioParam === null || ratioParam === '') {
            if (deltxLegacy !== null && deltxLegacy !== '') {
                const d = parseFloat(deltxLegacy);
                if (Number.isFinite(d)) {
                    const fLen = this.stereoFocalLength();
                    this.stereoRatio = fLen > 1e-12 ? d / fLen : 0;
                }
            }
        }
        const convParam = url.searchParams.get('conv');
        if (convParam !== null && convParam !== '') {
            const v = parseFloat(convParam);
            if (Number.isFinite(v)) {
                this.conv = v;
            }
        }
    }

    remove() {
        const { scene } = this;

        this.controller.destroy();
        this.controller = null;

        // cleanup render passes
        this.clearPass?.destroy();
        this.mainPass?.destroy();
        this.splatPass?.destroy();
        this.gizmoPass?.destroy();
        this.finalPass?.destroy();
        this.leftClearPass?.destroy();
        this.leftMainPass?.destroy();
        this.leftSplatPass?.destroy();
        this.leftGizmoPass?.destroy();
        this.rightClearPass?.destroy();
        this.rightMainPass?.destroy();
        this.rightSplatPass?.destroy();
        this.rightGizmoPass?.destroy();
        this.stereoFinalPass?.destroy();
        (this.finalPass?.renderable as ShaderQuad)?.destroy?.();
        (this.stereoFinalPass?.renderable as ShaderQuad)?.destroy?.();
        this.destroyRenderableTargets();
        this.camera.renderPasses = null;

        scene.cameraRoot.removeChild(this.mainCamera);

        this.picker.destroy();
        this.picker = null;

        scene.events.off('scene.boundChanged', this.onBoundChanged, this);
    }

    // handle the scene's bound changing. the camera must be configured to render
    // the entire extents as well as possible.
    // also update the existing camera distance to maintain the current view
    onBoundChanged(bound: BoundingBox) {
        const prevDistance = this.distanceTween.value.distance * this.sceneRadius;
        this.sceneRadius = Math.max(1e-03, bound.halfExtents.length());
        this.setDistance(prevDistance / this.sceneRadius, 0);
    }

    /**
     * 'camera.conv.adjust'
     * 'camera.stereoRatio.adjust'
     */

    onConvDecrease() {
        this.conv = Math.max(0, this.conv - 1);
        console.log('[ onConvDecrease ] >', this.conv);
        this.scene.events.fire('camera.conv.adjust', this.conv);
 
    }

    onConvIncrease() {
        this.conv += 1;
        this.scene.events.fire('camera.conv.adjust', this.conv);
        console.log('[ onConvIncrease ] >', this.conv);
    }

    onStereoRatioDecrease() {
        this.stereoRatio = Math.max(0, this.stereoRatio - 0.001);
        console.log('[ onStereoRatioDecrease ] >', this.stereoRatio);
        this.scene.events.fire('camera.stereoRatio.adjust', this.stereoRatio);
    }

    onStereoRatioIncrease() {
        this.stereoRatio += 0.001;
        console.log('[ onStereoRatioIncrease ] >', this.stereoRatio);
        this.scene.events.fire('camera.stereoRatio.adjust', this.stereoRatio);
    }

    serialize(serializer: Serializer) {
        serializer.packa(this.worldTransform.data);
        serializer.pack(
            this.fov,
            this.tonemapping,
            this.targetSize.width,
            this.targetSize.height,
            this.sideBySide,
            this.stereoRatio,
            this.conv
        );
    }

    // handle the viewer canvas resizing
    rebuildRenderTargets() {
        const { width, height } = this.targetSize;
        const { scene } = this;
        const useStereo = this.sideBySide && !this.targetSizeOverride;
        // const eyeW = useStereo ? Math.max(1, Math.floor(width / 2)) : width;
        const eyeW = 2560;
        const eyeH = height;
        const mode: 'mono' | 'stereo' = useStereo ? 'stereo' : 'mono';

        if (mode === 'mono' && this.mainTarget && this.mainTarget.width === width && this.mainTarget.height === height && this._layoutMode === 'mono') {
            return;
        }
        if (mode === 'stereo' && this.leftMainTarget && this.leftMainTarget.width === eyeW && this.leftMainTarget.height === eyeH && this._layoutMode === 'stereo') {
            return;
        }

        if (this._layoutMode === mode && ((mode === 'mono' && this.mainTarget) || (mode === 'stereo' && this.leftMainTarget))) {
            if (mode === 'mono') {
                this.resizeMonoTargets(width, height);
            } else {
                this.resizeStereoTargets(eyeW, eyeH);
            }
            this.setCameraAspectForLayout(mode === 'mono' ? width : eyeW, eyeH);
            scene.events.fire('camera.resize', { width, height });
            return;
        }

        this.destroyRenderableTargets();

        if (mode === 'mono') {
            this.createMonoTargets(width, height);
            this.initMonoRenderPasses();
            this.camera.renderPasses = [this.clearPass, this.mainPass, this.splatPass, this.gizmoPass, this.finalPass];
        } else {
            this.createStereoTargets(eyeW, eyeH);
            this.initStereoRenderPasses();
            this.camera.renderPasses = [
                this.leftClearPass, this.leftMainPass, this.leftSplatPass, this.leftGizmoPass,
                this.rightClearPass, this.rightMainPass, this.rightSplatPass, this.rightGizmoPass,
                this.stereoFinalPass
            ];
        }

        this._layoutMode = mode;
        this.setCameraAspectForLayout(mode === 'mono' ? width : eyeW, eyeH);
        scene.events.fire('camera.resize', { width, height });
    }

    private createTexture(name: string, w: number, h: number, format: number) {
        return new Texture(this.scene.graphicsDevice, {
            name,
            width: w,
            height: h,
            format,
            mipmaps: false,
            minFilter: FILTER_NEAREST,
            magFilter: FILTER_NEAREST,
            addressU: ADDRESS_CLAMP_TO_EDGE,
            addressV: ADDRESS_CLAMP_TO_EDGE
        });
    }

    private destroyRenderableTargets() {
        this.mainTarget?.destroy();
        this.splatTarget?.destroy();
        this.colorTarget?.destroy();
        this.workTarget?.destroy();
        this.leftMainTarget?.destroy();
        this.leftSplatTarget?.destroy();
        this.leftColorTarget?.destroy();
        this.leftWorkTarget?.destroy();
        this.rightMainTarget?.destroy();
        this.rightSplatTarget?.destroy();
        this.rightColorTarget?.destroy();
        this.rightWorkTarget?.destroy();
        this.mainTarget = undefined;
        this.splatTarget = undefined;
        this.colorTarget = undefined;
        this.workTarget = undefined;
        this.leftMainTarget = undefined;
        this.leftSplatTarget = undefined;
        this.leftColorTarget = undefined;
        this.leftWorkTarget = undefined;
        this.rightMainTarget = undefined;
        this.rightSplatTarget = undefined;
        this.rightColorTarget = undefined;
        this.rightWorkTarget = undefined;
    }

    private resizeMonoTargets(width: number, height: number) {
        this.mainTarget.resize(width, height);
        this.workTarget.resize(width, height);
        this.colorTarget.resize(width, height);
        this.splatTarget.resize(width, height);
    }

    private resizeStereoTargets(eyeW: number, eyeH: number) {
        this.leftMainTarget.resize(eyeW, eyeH);
        this.leftWorkTarget.resize(eyeW, eyeH);
        this.leftColorTarget.resize(eyeW, eyeH);
        this.leftSplatTarget.resize(eyeW, eyeH);
        this.rightMainTarget.resize(eyeW, eyeH);
        this.rightWorkTarget.resize(eyeW, eyeH);
        this.rightColorTarget.resize(eyeW, eyeH);
        this.rightSplatTarget.resize(eyeW, eyeH);
    }

    private createMonoTargets(width: number, height: number) {
        const colorBuffer = this.createTexture('cameraColor', width, height, PIXELFORMAT_RGBA16F);
        const workBuffer = this.createTexture('workColor', width, height, PIXELFORMAT_RGBA8);
        const depthBuffer = this.createTexture('cameraDepth', width, height, PIXELFORMAT_DEPTH);

        this.mainTarget = new RenderTarget({
            colorBuffer,
            depthBuffer,
            flipY: false,
            autoResolve: false
        });

        this.splatTarget = new RenderTarget({
            colorBuffers: [colorBuffer, workBuffer],
            depthBuffer,
            flipY: false,
            autoResolve: false
        });

        this.colorTarget = new RenderTarget({
            colorBuffer,
            depth: false,
            autoResolve: false
        });

        this.workTarget = new RenderTarget({
            colorBuffer: workBuffer,
            depth: false,
            autoResolve: false
        });

        this.picker.setRenderTargets(this.colorTarget, this.workTarget);
    }

    private createStereoTargets(eyeW: number, eyeH: number) {
        const mk = (side: string) => {
            const colorBuffer = this.createTexture(`${side}CameraColor`, eyeW, eyeH, PIXELFORMAT_RGBA16F);
            const workBuffer = this.createTexture(`${side}WorkColor`, eyeW, eyeH, PIXELFORMAT_RGBA8);
            const depthBuffer = this.createTexture(`${side}CameraDepth`, eyeW, eyeH, PIXELFORMAT_DEPTH);
            const mainTarget = new RenderTarget({
                colorBuffer,
                depthBuffer,
                flipY: false,
                autoResolve: false
            });
            const splatTarget = new RenderTarget({
                colorBuffers: [colorBuffer, workBuffer],
                depthBuffer,
                flipY: false,
                autoResolve: false
            });
            const colorTarget = new RenderTarget({
                colorBuffer,
                depth: false,
                autoResolve: false
            });
            const workTarget = new RenderTarget({
                colorBuffer: workBuffer,
                depth: false,
                autoResolve: false
            });
            return { mainTarget, splatTarget, colorTarget, workTarget };
        };

        const L = mk('left');
        this.leftMainTarget = L.mainTarget;
        this.leftSplatTarget = L.splatTarget;
        this.leftColorTarget = L.colorTarget;
        this.leftWorkTarget = L.workTarget;

        const R = mk('right');
        this.rightMainTarget = R.mainTarget;
        this.rightSplatTarget = R.splatTarget;
        this.rightColorTarget = R.colorTarget;
        this.rightWorkTarget = R.workTarget;

        this.picker.setRenderTargets(this.leftColorTarget, this.leftWorkTarget);
    }

    private initMonoRenderPasses() {
        const { scene } = this;

        this.camera.calculateProjection = null;

        this.clearPass.init(this.splatTarget);
        this.clearPass.setClearColor(new Color(0, 0, 0, 0));
        this.clearPass.setClearDepth(1);
        this.clearPass.setClearStencil(0);

        this.mainPass.init(this.mainTarget);
        this.mainPass.renderActions.length = 0;
        this.mainPass.addLayer(this.camera, scene.worldLayer, false, false);
        this.mainPass.addLayer(this.camera, scene.worldLayer, true, false);

        this.splatPass.init(this.splatTarget);
        this.splatPass.renderActions.length = 0;
        this.splatPass.addLayer(this.camera, scene.splatLayer, false, false);
        this.splatPass.addLayer(this.camera, scene.splatLayer, true, false);

        this.gizmoPass.init(this.mainTarget);
        this.gizmoPass.renderActions.length = 0;
        this.gizmoPass.addLayer(this.camera, scene.gizmoLayer, false, false);
        this.gizmoPass.addLayer(this.camera, scene.gizmoLayer, true, false);
        this.gizmoPass.renderActions[0].clearDepth = true;
        this.gizmoPass.renderActions[0].clearStencil = true;

        this.finalPass.init(null);
        this._stereoEyePass = null;
    }

    private initStereoRenderPasses() {
        const { scene } = this;
        const bindEye = (clearPass: RenderPass, isLeft: boolean) => {
            clearPass.before = () => {
                this._stereoEyePass = isLeft ? 'left' : 'right';
                this.applyStereoEye(isLeft);
                this.updateCameraUniforms();
                this.fitClippingPlanes(this.mainCamera.getLocalPosition(), this.mainCamera.forward);
                this.mainCamera.camera.camera._updateViewProjMat();
            };
        };

        bindEye(this.leftClearPass, true);
        bindEye(this.rightClearPass, false);

        this.stereoFinalPass.before = () => {
            this._stereoEyePass = null;
            this.mainCamera.setLocalPosition(this.stereoCenterPosition);
            this.mainCamera.camera.camera._updateViewProjMat();
        };

        const setupSide = (
            clearPass: RenderPass,
            mainPass: RenderPassForward,
            splatPass: RenderPassForward,
            gizmoPass: RenderPassForward,
            mainTarget: RenderTarget,
            splatTarget: RenderTarget
        ) => {
            clearPass.init(splatTarget);
            clearPass.setClearColor(new Color(0, 0, 0, 0));
            clearPass.setClearDepth(1);
            clearPass.setClearStencil(0);

            mainPass.init(mainTarget);
            mainPass.renderActions.length = 0;
            mainPass.addLayer(this.camera, scene.worldLayer, false, false);
            mainPass.addLayer(this.camera, scene.worldLayer, true, false);

            splatPass.init(splatTarget);
            splatPass.renderActions.length = 0;
            splatPass.addLayer(this.camera, scene.splatLayer, false, false);
            splatPass.addLayer(this.camera, scene.splatLayer, true, false);

            gizmoPass.init(mainTarget);
            gizmoPass.renderActions.length = 0;
            gizmoPass.addLayer(this.camera, scene.gizmoLayer, false, false);
            gizmoPass.addLayer(this.camera, scene.gizmoLayer, true, false);
            gizmoPass.renderActions[0].clearDepth = true;
            gizmoPass.renderActions[0].clearStencil = true;
        };

        setupSide(
            this.leftClearPass, this.leftMainPass, this.leftSplatPass, this.leftGizmoPass,
            this.leftMainTarget, this.leftSplatTarget
        );
        setupSide(
            this.rightClearPass, this.rightMainPass, this.rightSplatPass, this.rightGizmoPass,
            this.rightMainTarget, this.rightSplatTarget
        );

        this.stereoFinalPass.init(null);

        // Match 3DGS-style stereo: shear * P with a = x_off / f, b = y_off / f (see Python full_proj).
        this.camera.calculateProjection = (projMat: Mat4) => {
            this.applyStereoProjectionShear(projMat);
        };
    }

    private setCameraAspectForLayout(viewW: number, viewH: number) {
        this.camera.horizontalFov = viewW > viewH;
        this.camera.aspectRatio = viewW / viewH;
    }

    /** Offset each eye along camera local +X from {@link stereoCenterPosition} (same frame as onUpdate). */
    applyStereoEye(isLeft: boolean) {
        this.mainCamera.getLocalTransform().getX(va);
        if (va.lengthSq() > 1e-20) {
            va.normalize();
        }
        const dx = this.stereoDeltaX();
        const s = isLeft ? -dx : dx;
        vec.copy(this.stereoCenterPosition);
        vec.add(va.mulScalar(s));
        this.mainCamera.setLocalPosition(vec);
    }

    /** Half interocular distance in scene units: {@link stereoRatio} × {@link stereoFocalLength}. */
    stereoDeltaX(): number {
        return this.stereoRatio * this.stereoFocalLength();
    }

    /**
     * Normalized focal distance (scene units) for horizontal stereo: same scaling as shear term f in a = x_off/f.
     * Matches fx ∝ 1/tan(hfov/2) or fy-derived horizontal extent when using vertical FOV.
     */
    private stereoFocalLength(): number {
        const half = this.fov * 0.5 * math.DEG_TO_RAD;
        const t = Math.tan(half);
        if (t < 1e-12) {
            return 1;
        }
        const { horizontalFov, aspectRatio } = this.camera;
        return horizontalFov ? 1 / t : aspectRatio / t;
    }

    /**
     * Left-multiply projection by shear 
     * [
     *    [1,0,0,0],
     *    [0,1,0,0],
     *    [-a,b,1,0],
     *    [0,0,0,1]
     * ] with 
     * a = x_off/f = ±{@link stereoRatio}, 
     * b = y_off/f (here 0); 
     * matches 3DGS full_proj shear.
     */
    private applyStereoProjectionShear(projMat: Mat4) {
        if (!this._stereoEyePass || this.ortho) {
            return;
        }
        const r = this.conv //conv
        console.log('[ --->stereoRatio, conv] >', this._stereoEyePass, r);
        const a = this._stereoEyePass === 'left' ? -r : r;
        const b = 0;
        const d = stereoShearMat.data;
        d[0] = 1;
        d[1] = 0;
        d[2] = 0;
        d[3] = 0;
        d[4] = 0;
        d[5] = 1;
        d[6] = b;
        d[7] = 0;
        d[8] = 0;
        d[9] = 0;
        d[10] = 1;
        d[11] = 0;
        // d[12] = -a;
        d[12] = a;
        d[13] = 0;
        d[14] = 0;
        d[15] = 1;
        stereoProjPreShear.copy(projMat);
        projMat.mul2(stereoShearMat, stereoProjPreShear);
    }

    onUpdate(deltaTime: number) {
        // controller update
        this.controller.update(deltaTime);

        // update underlying values
        this.focalPointTween.update(deltaTime);
        this.azimElevTween.update(deltaTime);
        this.distanceTween.update(deltaTime);

        const azimElev = this.azimElevTween.value;
        const distance = this.distanceTween.value;

        Camera.calcForwardVec(forwardVec, azimElev.azim, azimElev.elev);

        if (this.lookCameraPos) {
            cameraPosition.copy(this.lookCameraPos);
            if (this.azimElevTween.timer >= this.azimElevTween.transitionTime) {
                this.lookCameraPos = null;
            }
        } else {
            cameraPosition.copy(forwardVec);
            cameraPosition.mulScalar(distance.distance * this.sceneRadius / this.fovFactor);
            cameraPosition.add(this.focalPointTween.value);
        }

        this.stereoCenterPosition.copy(cameraPosition);
        this.mainCamera.setLocalPosition(cameraPosition);
        this.mainCamera.setLocalEulerAngles(azimElev.elev, azimElev.azim, 0);

        this.fitClippingPlanes(this.mainCamera.getLocalPosition(), this.mainCamera.forward);

        const { camera } = this.mainCamera;
        const { targetSize } = this;

        // update ortho height
        camera.orthoHeight = this.distanceTween.value.distance * this.sceneRadius / this.fovFactor * (this.fov / 90) * (camera.horizontalFov ? targetSize.height / targetSize.width : 1);
        camera.camera._updateViewProjMat();
    }

    fitClippingPlanes(cameraPosition: Vec3, forwardVec: Vec3) {
        const bound = this.scene.bound;
        const boundRadius = bound.halfExtents.length();

        vec.sub2(bound.center, cameraPosition);
        const dist = vec.dot(forwardVec);

        if (dist > 0) {
            this.far = dist + boundRadius;
            // if camera is placed inside the sphere bound calculate near based far
            this.near = Math.max(1e-6, dist < boundRadius ? this.far / (1024 * 16) : dist - boundRadius);
        } else {
            // if the scene is behind the camera
            this.far = boundRadius * 2;
            this.near = this.far / (1024 * 16);
        }
    }

    onPreRender() {
        this.rebuildRenderTargets();
        this.updateCameraUniforms();
    }

    onPostRender() {

    }

    focus(options?: { focalPoint: Vec3, radius: number, speed: number }) {
        const getSplatFocalPoint = () => {
            for (const element of this.scene.elements) {
                if (element.type === ElementType.splat) {
                    const focalPoint = (element as Splat).focalPoint?.();
                    if (focalPoint) {
                        return focalPoint;
                    }
                }
            }
        };

        const focalPoint = options ? options.focalPoint : (getSplatFocalPoint() ?? this.scene.bound.center);
        const focalRadius = options ? options.radius : this.scene.bound.halfExtents.length();

        const fdist = focalRadius / this.sceneRadius;

        this.setDistance(isFinite(fdist) ? fdist : 1, options?.speed ?? 0);
        this.setFocalPoint(focalPoint, options?.speed ?? 0);
    }

    get fovFactor() {
        // use the larger axis fov (which is always this.fov) so camera distance
        // stays constant regardless of viewport aspect ratio.
        return Math.sin(this.fov * math.DEG_TO_RAD * 0.5);
    }

    getRay(screenX: number, screenY: number, ray: Ray) {
        const { camera, ortho } = this;
        const cameraPos = this.mainCamera.getPosition();

        // create the pick ray in world space
        if (ortho) {
            camera.screenToWorld(screenX, screenY, -1.0, vec);
            camera.screenToWorld(screenX, screenY, 1.0, vecb);
            vecb.sub(vec).normalize();
            ray.set(vec, vecb);
        } else {
            camera.screenToWorld(screenX, screenY, 1.0, vec);
            vec.sub(cameraPos).normalize();
            ray.set(cameraPos, vec);
        }
    }

    // intersect the scene at the given normalized screen coordinate (0-1 range) using depth picking
    async intersect(x: number, y: number) {
        const { scene } = this;
        const splats = scene.getElementsByType(ElementType.splat);

        let closestDepth = Infinity;
        let closestSplat: Splat | null = null;

        // Find the splat with the smallest depth at this screen position
        for (let i = 0; i < splats.length; ++i) {
            const splat = splats[i] as Splat;

            this.picker.prepareDepth(splat);
            const normalizedDepth = await this.picker.readDepth(x, y);

            if (normalizedDepth !== null && normalizedDepth < closestDepth) {
                closestDepth = normalizedDepth;
                closestSplat = splat;
            }
        }

        if (!closestSplat) {
            return null;
        }

        // Convert normalized depth to linear depth
        const linearDepth = closestDepth * (this.far - this.near) + this.near;

        // Convert normalized coordinates to screen pixels for getRay
        const screenX = x * scene.canvas.clientWidth;
        const screenY = y * scene.canvas.clientHeight;

        // Calculate world position from ray and depth
        this.getRay(screenX, screenY, ray);
        const t = linearDepth / ray.direction.dot(this.mainCamera.forward);
        const position = new Vec3();
        position.copy(ray.origin).add(vec.copy(ray.direction).mulScalar(t));

        return {
            splat: closestSplat,
            position: position,
            distance: t
        };
    }

    // intersect the scene at the normalized screen location (0-1 range) and focus the camera on this location
    async pickFocalPoint(x: number, y: number) {
        const result = await this.intersect(x, y);
        if (result) {
            const { scene } = this;

            this.setFocalPoint(result.position);
            this.setDistance(result.distance / this.sceneRadius * this.fovFactor);
            scene.events.fire('camera.focalPointPicked', {
                camera: this,
                splat: result.splat,
                position: result.position
            });
        }
    }

    // pick mode

    // render picker contents
    pickPrep(splat: Splat, mode: 'add' | 'remove' | 'set') {
        this.picker.prepareId(splat, mode);
    }

    pick(x: number, y: number) {
        return this.picker.readId(x, y);
    }

    pickRect(x: number, y: number, width: number, height: number) {
        return this.picker.readIds(x, y, width, height);
    }

    docSerialize() {
        const pack3 = (v: Vec3) => [v.x, v.y, v.z];

        return {
            focalPoint: pack3(this.focalPointTween.target),
            azim: this.azim,
            elev: this.elevation,
            distance: this.distance,
            fov: this.fov,
            tonemapping: this.tonemapping
        };
    }

    docDeserialize(settings: any) {
        this.setFocalPoint(new Vec3(settings.focalPoint), 0);
        this.setAzimElev(settings.azim, settings.elev, 0);
        this.setDistance(settings.distance, 0);
        this.fov = settings.fov;
        this.tonemapping = settings.tonemapping;
    }

    // offscreen render mode

    startOffscreenMode(width: number, height: number) {
        this.targetSizeOverride = { width, height };
        this.finalPass.enabled = false;
        this.rebuildRenderTargets();
        this.onUpdate(0);
    }

    endOffscreenMode() {
        this.targetSizeOverride = null;
        this.finalPass.enabled = true;
        this.rebuildRenderTargets();
        this.onUpdate(0);
    }

    get targetSize() {
        return this.targetSizeOverride ?? this.scene.targetSize;
    }

    /** Stereo RT pipeline active (not offscreen export). */
    get isStereoDisplayActive() {
        return this.sideBySide && !this.targetSizeOverride;
    }

    /**
     * Splat overlay (work) color buffer for outline/underlay: mono uses workTarget; stereo uses the eye being rendered.
     */
    get overlayWorkColorBuffer(): Texture | undefined {
        if (!this.isStereoDisplayActive) {
            return this.workTarget?.colorBuffer;
        }
        if (this._stereoEyePass === 'right') {
            return this.rightWorkTarget?.colorBuffer;
        }
        return this.leftWorkTarget?.colorBuffer;
    }

    /**
     * Unproject screen pixels for pan/zoom using full-frame aspect; stereo rendering still uses per-eye aspect.
     */
    screenToWorldForControls(screenX: number, screenY: number, cameraz: number, out: Vec3) {
        const comp = this.mainCamera.camera;
        if (!this.isStereoDisplayActive) {
            comp.screenToWorld(screenX, screenY, cameraz, out);
            return;
        }
        const tw = this.targetSize.width;
        const th = this.targetSize.height;
        const eyeW = Math.max(1, Math.floor(tw / 2));
        comp.aspectRatio = tw / th;
        comp.horizontalFov = tw > th;
        comp.camera._updateViewProjMat();
        comp.screenToWorld(screenX, screenY, cameraz, out);
        comp.aspectRatio = eyeW / th;
        comp.horizontalFov = eyeW > th;
        comp.camera._updateViewProjMat();
    }

    get camera() {
        return this.mainCamera.camera;
    }

    get worldTransform() {
        return this.mainCamera.getWorldTransform();
    }

    get position() {
        return this.mainCamera.getPosition();
    }

    get forward() {
        return this.mainCamera.forward;
    }
}

export { Camera };